/**
 * useFileActions — what each file offers, in every view of the workspace.
 *
 * Browse's list and grid, Search's results and the Desktop's context menu
 * all render from `actionsFor(doc)`, so "may I move / rename / delete this
 * file here?" is answered once (`lib/file-capabilities.ts`, on the client
 * mirror of the file-area policy) instead of once per view. The hook also
 * owns the dialogs those actions open — Move, Give an arche access, the
 * My-Fias delete confirmation, Delete permanently — and one notice banner
 * for what happened. Render `ui` once, anywhere in the page.
 *
 * `useDocumentPlacement` set this pattern for drops; this is the same idea
 * for the rest of the menu. The server re-decides every action.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, type VaultDocumentDto, type VaultDocumentFolderDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { archeNamespaceOfPath, vaultAreaOfPath, type VaultArea } from '@/lib/vault-areas';
import {
  useRestoreVaultDocument,
  useSoftDeleteVaultDocument,
  useUpdateVaultDocument,
} from './useVaultDocuments';
import {
  GiveArcheAccessDialog,
  archeNameFor,
  archesWithFolders,
  type DocumentPlacementController,
} from '../components/DocumentPlacement';
import { MoveDocumentDialog } from '../components/shared/MoveDocumentDialog';
import { PurgeDocumentDialog } from '../components/shared/TrashDialogs';
import { NoticeBanner, type Notice } from '../components/shared/NoticeBanner';
import { downloadVaultDocument } from '../lib/download';
import {
  actorContextOf,
  fileCapabilities,
  type FileActorContext,
  type FileCapabilities,
} from '../lib/file-capabilities';
import { runBounded } from '../lib/run-bounded';
import { ARCHE_ID } from '../constants';

/** The actions one file offers here. An absent action is not available —
 *  views render only what is present. */
export interface FileActionSet {
  viewDetails: () => void;
  download: () => void;
  move?: () => void;
  giveArcheAccess?: () => void;
  /** Rename in place; resolves true when it saved. */
  rename?: (name: string) => Promise<boolean>;
  softDelete?: () => void;
  restore?: () => void;
  purge?: () => void;
}

export interface FileActionsController {
  actionsFor: (doc: VaultDocumentDto) => FileActionSet;
  capabilitiesOf: (doc: VaultDocumentDto) => FileCapabilities;
  areaOf: (doc: Pick<VaultDocumentDto, 'folderId'>) => VaultArea;
  actor: FileActorContext;
  /** Move several files to the Trash (a drop on the Desktop's Trash, Delete
   *  on a selection). Files this person may not delete here are skipped and
   *  reported; arche files (My-Fias) ask first. */
  trash: (docs: readonly VaultDocumentDto[]) => void;
  /** Report something from outside the hook through its banner. */
  notify: (notice: Notice) => void;
  ui: ReactNode;
}

/** Trash requests in flight at once for a multi-select. */
const TRASH_CONCURRENCY = 4;

function friendlyActionError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // The area policy's refusals carry their own user-facing message.
    if (
      err.code === 'AREA_READ_ONLY' ||
      err.code === 'AREA_ADMIN_REQUIRED' ||
      err.code === 'NOT_MOVABLE' ||
      err.code === 'VIEWER_READ_ONLY'
    ) {
      return err.message;
    }
    if (err.status === 403) return "You don't have permission to do that here.";
    if (err.status === 404) return 'That file no longer exists.';
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

export function useFileActions(input: {
  holderId: string;
  holder: HolderSwitcherOption | null | undefined;
  folders: readonly VaultDocumentFolderDto[] | undefined;
  placement: DocumentPlacementController;
}): FileActionsController {
  const { holderId, placement } = input;
  const navigate = useNavigate();
  const folders = useMemo(() => input.folders ?? [], [input.folders]);
  const holderType = input.holder?.holderType;
  const role = input.holder?.role;
  const actor = useMemo(
    () => actorContextOf(holderType && role ? { holderType, role } : null),
    [holderType, role],
  );
  const softDelete = useSoftDeleteVaultDocument();
  const restore = useRestoreVaultDocument();
  const update = useUpdateVaultDocument();

  const [notice, setNotice] = useState<Notice | null>(null);
  const [movingDoc, setMovingDoc] = useState<VaultDocumentDto | null>(null);
  const [accessDoc, setAccessDoc] = useState<VaultDocumentDto | null>(null);
  const [purgingDoc, setPurgingDoc] = useState<VaultDocumentDto | null>(null);
  /** Trash waiting on the My-Fias confirmation. */
  const [confirmTrash, setConfirmTrash] = useState<{
    docs: VaultDocumentDto[];
    skipped: number;
  } | null>(null);

  const folderPathById = useMemo(
    () => new Map(folders.map((f) => [f.folderId, f.path])),
    [folders],
  );
  const areaOf = useCallback<FileActionsController['areaOf']>(
    (doc) => vaultAreaOfPath(doc.folderId ? (folderPathById.get(doc.folderId) ?? null) : null),
    [folderPathById],
  );
  const capabilitiesOf = useCallback(
    (doc: VaultDocumentDto) => fileCapabilities(doc, areaOf(doc), actor),
    [areaOf, actor],
  );

  const runTrash = useCallback(
    async (docs: VaultDocumentDto[], skipped: number) => {
      const results = await runBounded(docs, TRASH_CONCURRENCY, (doc) =>
        softDelete.mutateAsync(doc.documentId),
      );
      const failed = results
        .map((r, i) => ({ r, doc: docs[i] }))
        .filter((x) => x.r.status === 'rejected');
      const moved = docs.length - failed.length;
      if (docs.length === 1 && skipped === 0) {
        // One file: say nothing on success (the list shows it), explain a failure.
        if (failed.length === 1) {
          const reason = (failed[0].r as PromiseRejectedResult).reason;
          setNotice({
            tone: 'error',
            message: friendlyActionError(reason, 'That didn’t work. Try again.'),
          });
        }
        return;
      }
      const parts = [`Moved ${moved} of ${docs.length + skipped} to the Trash.`];
      if (skipped > 0) {
        parts.push(`${skipped} ${skipped === 1 ? 'isn’t' : 'aren’t'} yours to delete here.`);
      }
      setNotice({
        tone: failed.length > 0 ? 'error' : 'info',
        message: parts.join(' '),
        details: failed.map(
          ({ r, doc }) =>
            `“${doc.name}”: ${friendlyActionError((r as PromiseRejectedResult).reason, 'failed')}`,
        ),
      });
    },
    [softDelete],
  );

  const trash = useCallback<FileActionsController['trash']>(
    (docs) => {
      setNotice(null);
      const allowed = docs.filter((doc) => capabilitiesOf(doc).trash);
      const skipped = docs.length - allowed.length;
      if (allowed.length === 0) {
        if (docs.length > 0) {
          setNotice({
            tone: 'info',
            message:
              docs.length === 1
                ? 'Only workspace admins can delete an arche’s files in a shared workspace.'
                : 'None of these can be deleted by you here.',
          });
        }
        return;
      }
      if (allowed.some((doc) => capabilitiesOf(doc).trashNeedsConfirm)) {
        setConfirmTrash({ docs: allowed, skipped });
        return;
      }
      void runTrash(allowed, skipped);
    },
    [capabilitiesOf, runTrash],
  );

  const actionsFor = useCallback<FileActionsController['actionsFor']>(
    (doc) => {
      const can = capabilitiesOf(doc);
      return {
        viewDetails: () => navigate(`/a/${ARCHE_ID}/${doc.documentId}`),
        download: () => void downloadVaultDocument(doc),
        move: can.move ? () => setMovingDoc(doc) : undefined,
        giveArcheAccess: can.giveArcheAccess
          ? () => {
              setNotice(null);
              setAccessDoc(doc);
            }
          : undefined,
        rename: can.rename
          ? async (name: string) => {
              const next = name.trim();
              if (!next || next === doc.name) return true;
              try {
                await update.mutateAsync({ documentId: doc.documentId, updates: { name: next } });
                return true;
              } catch (err) {
                setNotice({
                  tone: 'error',
                  message: friendlyActionError(err, 'Couldn’t rename that file.'),
                });
                return false;
              }
            }
          : undefined,
        softDelete: can.trash ? () => trash([doc]) : undefined,
        restore: can.restore
          ? () =>
              restore.mutate(doc.documentId, {
                onError: (err) =>
                  setNotice({
                    tone: 'error',
                    message: friendlyActionError(err, 'Couldn’t restore that file.'),
                  }),
              })
          : undefined,
        purge: can.purge ? () => setPurgingDoc(doc) : undefined,
      };
    },
    [capabilitiesOf, navigate, restore, trash, update],
  );

  const confirmSingle =
    confirmTrash && confirmTrash.docs.length === 1 ? confirmTrash.docs[0] : null;
  const ui = useMemo(
    () => (
      <>
        {notice && <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} />}

        {accessDoc && (
          <GiveArcheAccessDialog
            doc={accessDoc}
            arches={archesWithFolders(folders)}
            onClose={() => setAccessDoc(null)}
            onDone={(message) => {
              setAccessDoc(null);
              setNotice({ tone: 'info', message });
            }}
          />
        )}

        <Dialog
          open={confirmTrash !== null}
          onOpenChange={(open) => !open && setConfirmTrash(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {confirmSingle ? 'Delete this arche file?' : 'Delete arche files?'}
              </DialogTitle>
              <DialogDescription>
                {confirmSingle ? (
                  <>
                    “{confirmSingle.name}” is one of{' '}
                    {archeNameFor(
                      archeNamespaceOfPath(
                        confirmSingle.folderId
                          ? (folderPathById.get(confirmSingle.folderId) ?? null)
                          : null,
                      ),
                      folders,
                    )}
                    ’s own files. The arche may lose work or settings it kept here. It goes to the
                    Trash, so you can restore it for 30 days.
                  </>
                ) : (
                  <>
                    Some of these files are arches’ own files. The arches may lose work or settings
                    they kept here. They go to the Trash, so you can restore them for 30 days.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setConfirmTrash(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  const pending = confirmTrash;
                  setConfirmTrash(null);
                  if (pending) void runTrash(pending.docs, pending.skipped);
                }}
              >
                Move to Trash
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <MoveDocumentDialog
          document={movingDoc}
          folders={folders}
          onPlace={placement.place}
          onClose={() => setMovingDoc(null)}
        />

        <PurgeDocumentDialog
          document={purgingDoc}
          holderId={holderId}
          onClose={() => setPurgingDoc(null)}
        />
      </>
    ),
    [
      notice,
      accessDoc,
      folders,
      confirmTrash,
      confirmSingle,
      folderPathById,
      runTrash,
      movingDoc,
      placement.place,
      purgingDoc,
      holderId,
    ],
  );

  // A stable object, so the views that hold it don't re-render for nothing.
  return useMemo(
    () => ({ actionsFor, capabilitiesOf, areaOf, actor, trash, notify: setNotice, ui }),
    [actionsFor, capabilitiesOf, areaOf, actor, trash, ui],
  );
}
